import React, { useEffect, useState } from 'react'
import Text from '@embracesbs/component-text'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import SupportLogin from '../Auth/SupportLogin'
import TenantMonitoring from './TenantMonitoring'
import { formatDateTime } from '../../Utilities/helpers/helpers'
import { useTenants } from '../Context/TenantContext'
import { useSubscriptions } from '../Context/SubscriptionContext'
import { useApi } from '../../Hooks/useApi'

interface Module {
  package_name: string | null
  package_version: string | null
  package_build_id: number | null
}

interface ModuleCollection {
  id: string
  tenant_id: string | null
  timestamp: string
  modules: Module[]
  isDeleted: boolean
  creationTime: string
  createdBy: string
  modifiedTime: string
  modifiedBy: string
}

interface tenantExtendedDetailsProbs {}

const TenantExtendedDetails: React.FC<tenantExtendedDetailsProbs> = ({}) => {
  const { selectedTenant, selectedTenantId } = useTenants()
  const { selectedSubscription } = useSubscriptions()
  const { fetchApi } = useApi()
  const [moduleCollection, setModuleCollection] = useState<ModuleCollection>()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!selectedTenantId) return

    setIsLoading(true)
    setError(null)
    setNotFound(false)

    fetchApi({
      endpoint: `/api/modulecollection/tenant/${selectedTenantId}`,
      method: 'GET'
    })
      .then((response) => {
        if (!response || !response.modules || response.modules.length === 0) {
          setNotFound(true)
        } else {
          setModuleCollection(response)
        }
      })
      .catch((err) => {
        setNotFound(true)
        console.error('Error fetching module versions:', err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [selectedTenantId, fetchApi])

  if (!selectedTenant) {
    return null
  }

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--tma-page-bg)',
          margin: '0 -20px 0 -40px',
          padding: '0 20px 14px 40px'
        }}
      >
        <div style={{ height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Text textStyle='h6' style={{ fontSize: '16px' }}>
              Tenant Properties
            </Text>
          </div>
          <div style={{ borderTop: '1px solid var(--tma-text-faint)', marginLeft: '-40px', marginRight: '-20px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '14px' }}>
          {selectedTenant.deployStatus !== 'initializing' && <SupportLogin tenant={selectedTenant} isFullWidth variant='outlined' />}
          <TenantMonitoring tenant={selectedTenant} />
          <Button2
            style={{ width: '100%', justifyContent: 'center' }}
            buttonSize='30px'
            buttonRadius='8px'
            variant='outlined'
            leftElement={<Icon iconName='ConstructionPipe1' size={16} />}
            text='Tenant Pipeline'
            onClick={() =>
              window.open(
                `https://dev.azure.com/embrace/Embrace%20Suite/_build?definitionId=${selectedTenant.azureDevopsBuildDefinitionId}`,
                '_blank',
                'noopener,noreferrer'
              )
            }
          />
        </div>
      </div>
      <Text textColor='grey' textStyle='description'>
        Name:
      </Text>
      <Text textStyle='body'>{selectedTenant.tenantName}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        Display name:
      </Text>
      <Text textStyle='body'>{selectedTenant.tenantDisplayname}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        Subscription:
      </Text>
      <Text textStyle='body'>{selectedSubscription?.customerName}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        Environment:
      </Text>
      <Text textStyle='body'>{selectedTenant.environment}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        CreatedBy:
      </Text>
      <Text textStyle='body'>{selectedTenant.createdBy}</Text>
      <br></br>
      <Text textColor='grey' textStyle='description'>
        CreationTime:
      </Text>
      <Text textStyle='body'>{formatDateTime(selectedTenant.creationTime)}</Text>
      <br></br>
      {selectedTenant.modifiedBy && (
        <>
          <Text textColor='grey' textStyle='description'>
            LastModifiedBy:
          </Text>
          <Text textStyle='body'>{selectedTenant.modifiedBy}</Text>
          <br />
        </>
      )}
      <Text textStyle='h6' style={{ fontSize: '16px' }}>
        Module Versions
      </Text>
      <div style={{ borderTop: '1px solid var(--tma-text-faint)', margin: '8px -20px 12px -40px' }} />

      {isLoading && <Text textStyle='body'>Loading module information...</Text>}

      {error && (
        <Text textStyle='body' textColor='error'>
          Error loading modules: {error}
        </Text>
      )}

      {notFound && <Text textStyle='body'>No module information found for this tenant</Text>}

      {!isLoading && !error && !notFound && moduleCollection && (
        <div>
          {moduleCollection.modules.length === 0 ? (
            <Text textStyle='body'>No modules found for this tenant</Text>
          ) : (
            moduleCollection.modules.map((module, index) => (
              <div key={index}>
                <Text textColor='grey' textStyle='description'>
                  {module.package_name || 'Unknown package'}:
                </Text>
                {module.package_build_id ? (
                  <Text textStyle='body'>
                    <a
                      href={`https://dev.azure.com/embrace/Embrace%20Suite/_build/results?buildId=${module.package_build_id}&view=results`}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        color: 'var(--tma-accent)',
                        textDecoration: 'underline'
                      }}
                    >
                      {module.package_version || 'Unknown version'}
                    </a>
                  </Text>
                ) : (
                  <Text textStyle='body'>{module.package_version || 'Unknown version'}</Text>
                )}
                <br></br>
              </div>
            ))
          )}
        </div>
      )}
    </>
  )
}

export default TenantExtendedDetails
